import { useStore } from '@nanostores/react';
import { memo, useCallback, useEffect, useState } from 'react';
import { Button } from '~/components/ui/Button';
import { LoadingDots } from '~/components/ui/LoadingDots';
import { getUserDomainList } from '~/lib/.client/api/domains';
import { domainsStore } from '~/lib/stores/domains';
import { classNames } from '~/utils/classNames';
import { withSpinner } from '~/utils/withSpinner';

export const DomainList = memo(() => {
  const userDomainList = useStore(domainsStore.enrichedDomains);
  const [isLoadingDomainList, setIsLoadingDomainList] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFetchUserDomainList = useCallback(async () => {
    setError(null);

    await withSpinner({
      action: async () => {
        try {
          const data = await getUserDomainList();

          if (data) {
            domainsStore.setUserDomainList(data);
          }
        } catch (error) {
          setError('Failed to fetch domains');
          console.error('Failed to fetch domains:', error);
        }
      },
      onStateUpdate: setIsLoadingDomainList,
    });
  }, []);

  useEffect(() => {
    handleFetchUserDomainList();
  }, []);

  if (isLoadingDomainList) {
    return (
      <LoadingDots className="text-sm py-4 text-bolt-ds-textTertiary justify-start" text="Loading custom domains" />
    );
  }

  if (!isLoadingDomainList && error) {
    return (
      <div className="flex justify-between py-4 text-sm text-bolt-ds-textTertiary">
        <div className="flex gap-2 items-center">
          <span className="i-heroicons:exclamation-circle block size-4 text-bolt-ds-danger" />
          <p className="text-bolt-ds-danger">
            Unable to load domains. Please try again, or contact support if the issue persists
          </p>
        </div>
        <Button size="sm" variant="tint" color="danger" onClick={handleFetchUserDomainList}>
          Retry
        </Button>
      </div>
    );
  }

  if (!userDomainList || userDomainList.length === 0) {
    return <p className="py-4 text-sm text-bolt-ds-textTertiary">No custom domains found</p>;
  }

  return (
    <>
      <p className="text-sm mt-2">Only custom domains purchased in Bolt are listed here.</p>
      <ul className="flex flex-col gap-2">
        {userDomainList.map((domain) => (
          <li
            key={domain.id}
            className="flex justify-between items-center gap-2 border py-3 px-4 border-bolt-elements-borderColor rounded-lg bg-bolt-ds-surfaceTwo"
          >
            <div className="flex max-w-7/9 gap-3">
              <span className="i-ph:globe size-6 text-bolt-ds-iconSecondary mt-0.5 shrink-0" />
              <div className="flex flex-col w-full gap-1">
                <h4 className="font-medium text-sm">{domain.name}</h4>
                <p className="text-sm text-xs text-bolt-ds-textSecondary truncate">
                  {domain.projectId ? (domain.projectName ? `Assigned to ${domain.projectName}` : '') : 'Unassigned'}
                </p>
              </div>
            </div>
            {domain.projectId && (
              <a
                className={classNames(
                  'font-medium decoration-none text-xs px-2 py-1.5 rounded-md shrink-0',
                  'bg-bolt-ds-inverseSurface/10 [&:hover:where(:not(:disabled))]:bg-bolt-ds-inverseSurface/15',
                )}
                href={`/p/${domain.projectId}`}
              >
                Go to project
              </a>
            )}
          </li>
        ))}
      </ul>
    </>
  );
});

DomainList.displayName = 'HostingDomainList';
